import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';

const languages = [
  { code: 'de', label: 'DE' },
  { code: 'en', label: 'EN' },
  { code: 'sq', label: 'SQ' },
  { code: 'it', label: 'IT' },
  { code: 'fr', label: 'FR' },
];

interface LanguageSwitcherProps {
  variant?: 'light' | 'dark';
}

export default function LanguageSwitcher({ variant = 'light' }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const isDark = variant === 'dark';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        aria-label={t('shared.select_language')}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-lg text-xs tracking-widest transition-colors cursor-pointer font-label uppercase border ${
          isDark
            ? 'text-white/80 hover:text-white border-white/25 hover:bg-white/10'
            : 'text-foreground-600 hover:text-foreground-900 border-background-200 hover:bg-background-100'
        }`}
      >
        <span>{current.label}</span>
        <i className={`text-[10px] transition-transform ${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-background-50 border border-background-200/70 rounded-lg py-1 min-w-[80px] z-50 shadow-sm">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleChange(lang.code)}
              className={`w-full text-left px-3 py-2 min-h-[40px] text-xs tracking-widest transition-colors cursor-pointer font-label uppercase ${
                lang.code === i18n.language
                  ? 'text-primary-500 bg-primary-50'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}